import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import ProfileIdentityCard from "@/components/ProfileIdentityCard";
import AppearanceCard from "@/components/AppearanceCard";
import NotificationsCard from "@/components/NotificationsCard";
import SmsCard from "@/components/SmsCard";
import Quick15Card from "@/components/Quick15Card";
import ReferralCard from "@/components/ReferralCard";
import { ShieldCheck, ShieldOff, Smartphone, KeyRound, Copy, Check, RefreshCw, AlertTriangle, Download, Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

function BackupCodesPanel({ codes, onClose }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join("\n");
  const copyAll = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  const download = () => {
    const blob = new Blob([
      "Withdraw Guard — Recovery Codes\n",
      "Each code works ONCE if you lose your authenticator.\n",
      "Store somewhere safe (password manager, printed copy).\n\n",
      ...codes.map((c) => c + "\n"),
    ], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "withdrawguard-backup-codes.txt";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-6 bg-ink text-cream rounded-2xl p-6" data-testid="backup-codes-panel">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-gold shrink-0 mt-0.5" strokeWidth={1.5} />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-gold">Save these now — shown only once</div>
          <h3 className="font-serif text-2xl mt-1">Recovery codes</h3>
          <p className="text-sm text-sand mt-2 leading-relaxed">
            Each code works <strong>once</strong>. Use them if you lose access to your authenticator app.
            Store them in a password manager or print them.
          </p>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-2 font-mono text-sm" data-testid="backup-codes-list">
        {codes.map((c) => (
          <div key={c} className="bg-white/5 border border-white/10 rounded-md px-3 py-2 tracking-wider">{c}</div>
        ))}
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <button onClick={copyAll}
          className="inline-flex items-center gap-2 bg-cream text-ink rounded-lg px-4 py-2 text-sm font-medium hover:bg-white transition-colors"
          data-testid="copy-all-codes">
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copied ? "Copied" : "Copy all"}
        </button>
        <button onClick={download}
          className="inline-flex items-center gap-2 bg-transparent border border-white/30 text-cream rounded-lg px-4 py-2 text-sm font-medium hover:bg-white/10 transition-colors"
          data-testid="download-codes">
          <Download className="w-4 h-4" /> Download .txt
        </button>
        <button onClick={onClose}
          className="ml-auto text-sm text-sand hover:text-white"
          data-testid="dismiss-codes">
          I&apos;ve saved them — dismiss
        </button>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [stage, setStage] = useState("idle"); // idle, setup, verify, disable, regen
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [bcStatus, setBcStatus] = useState(null);

  // Password change state
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwOk, setPwOk] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError(""); setPwOk(false);
    if (pwNew !== pwConfirm) {
      setPwError("New password and confirmation don't match.");
      return;
    }
    if (pwNew.length < 6) {
      setPwError("New password must be at least 6 characters.");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password",
        { current_password: pwCurrent, new_password: pwNew });
      setPwOk(true);
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
      setTimeout(() => { setPwOk(false); setPwOpen(false); }, 1800);
    } catch (e) {
      setPwError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setPwBusy(false); }
  };

  const loadStatus = async () => {
    if (!user?.two_factor_enabled) { setBcStatus(null); return; }
    try {
      const { data } = await api.get("/auth/2fa/backup-codes/status");
      setBcStatus(data);
    } catch (err) {
      console.warn("Failed to load backup codes status:", err);
    }
  };

  useEffect(() => { loadStatus(); }, [user?.two_factor_enabled]);

  if (!user) return <Navigate to="/login" replace />;

  const begin = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data); setStage("setup");
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const enable = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setBackupCodes(data.backup_codes);
      await refresh();
      setStage("idle"); setCode(""); setSetupData(null);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/2fa/disable", { code });
      await refresh();
      setStage("idle"); setCode(""); setBackupCodes(null); setBcStatus(null);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const regenerate = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/2fa/backup-codes/regenerate", { code });
      setBackupCodes(data.backup_codes);
      await loadStatus();
      setStage("idle"); setCode("");
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const copyKey = () => {
    if (!setupData) return;
    navigator.clipboard.writeText(setupData.secret);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Profile</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Account & security.</h1>

        <ProfileIdentityCard />
        <ReferralCard />
        <AppearanceCard />
        <NotificationsCard />
        <SmsCard />
        <Quick15Card />

        <div className="mt-10 bg-linen border border-sand rounded-2xl p-8" data-testid="profile-info">
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Name</div>
              <div className="font-serif text-xl mt-1">{user.name}{user.username ? <span className="font-mono text-sm text-sage ml-2">@{user.username}</span> : null}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Email</div>
              <div className="font-mono text-sm mt-1.5" data-testid="profile-email">{user.email}</div>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-linen border border-sand rounded-2xl p-8" data-testid="password-section">
          <div className="flex items-start gap-4">
            <Lock className="w-7 h-7 text-forest" strokeWidth={1.5} />
            <div className="flex-1">
              <h2 className="font-serif text-2xl">Password</h2>
              <p className="text-sm text-moss mt-2 leading-relaxed">
                Change the password you use to sign in.
              </p>
            </div>
            {!pwOpen && (
              <button onClick={() => { setPwOpen(true); setPwError(""); setPwOk(false); }}
                className="bg-forest text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-forest-deep transition-colors"
                data-testid="open-pw-form">
                Change
              </button>
            )}
          </div>

          {pwOpen && (
            <form onSubmit={changePassword} className="mt-6 max-w-md space-y-4" data-testid="change-password-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Current password</label>
                <input type="password" required value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)}
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  autoFocus data-testid="pw-current" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">New password</label>
                <input type="password" required minLength={6} value={pwNew} onChange={(e) => setPwNew(e.target.value)}
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  data-testid="pw-new" />
                <p className="mt-1.5 text-xs text-sage">Minimum 6 characters.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Confirm new password</label>
                <input type="password" required value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)}
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  data-testid="pw-confirm" />
              </div>
              {pwError && <div className="text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="pw-error">{pwError}</div>}
              {pwOk && <div className="text-sm text-white bg-forest px-4 py-3 rounded-lg" data-testid="pw-success">Password updated.</div>}
              <div className="flex gap-3">
                <button type="button" onClick={() => { setPwOpen(false); setPwCurrent(""); setPwNew(""); setPwConfirm(""); setPwError(""); setPwOk(false); }}
                  className="text-sm text-moss hover:text-ink" data-testid="pw-cancel">Cancel</button>
                <button type="submit" disabled={pwBusy || !pwCurrent || !pwNew || pwNew !== pwConfirm}
                  className="flex-1 bg-forest text-white rounded-lg px-5 py-3 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
                  data-testid="pw-submit">
                  {pwBusy ? "Saving…" : "Update password"}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="mt-6 bg-linen border border-sand rounded-2xl p-8" data-testid="two-factor-section">
          <div className="flex items-start gap-4">
            {user.two_factor_enabled
              ? <ShieldCheck className="w-7 h-7 text-forest" strokeWidth={1.5} />
              : <ShieldOff className="w-7 h-7 text-sage" strokeWidth={1.5} />}
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-serif text-2xl">Two-factor authentication</h2>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${
                  user.two_factor_enabled ? "bg-forest text-white" : "bg-sand-light text-moss"
                }`} data-testid="2fa-status">{user.two_factor_enabled ? "ENABLED" : "OFF"}</span>
              </div>
              <p className="text-sm text-moss mt-2 leading-relaxed">
                Add a one-time code from your authenticator app (Google Authenticator, Authy, 1Password) to every sign-in.
              </p>
            </div>
          </div>

          {/* IDLE — enabled */}
          {stage === "idle" && user.two_factor_enabled && (
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
              {bcStatus && (
                <div className="text-sm text-moss" data-testid="backup-codes-status">
                  Recovery codes: <span className="font-mono text-ink">{bcStatus.remaining}/{bcStatus.total}</span> unused
                  {bcStatus.remaining <= 2 && (
                    <span className="ml-2 text-gold font-medium">⚠ regenerate soon</span>
                  )}
                </div>
              )}
              <button onClick={() => { setStage("regen"); setError(""); setCode(""); }}
                className="text-sm text-forest hover:underline inline-flex items-center gap-1.5"
                data-testid="regen-backup-codes-btn">
                <RefreshCw className="w-3.5 h-3.5" /> Regenerate recovery codes
              </button>
              <button onClick={() => { setStage("disable"); setError(""); setCode(""); }}
                className="text-sm text-clay hover:underline" data-testid="disable-2fa-btn">
                Turn off 2FA
              </button>
            </div>
          )}

          {/* IDLE — disabled */}
          {stage === "idle" && !user.two_factor_enabled && (
            <button onClick={begin} disabled={busy}
              className="mt-6 bg-forest text-white rounded-lg px-5 py-2.5 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors inline-flex items-center gap-2"
              data-testid="enable-2fa-btn">
              <KeyRound className="w-4 h-4" strokeWidth={1.5} />
              {busy ? "Preparing…" : "Enable 2FA"}
            </button>
          )}

          {/* Setup */}
          {stage === "setup" && setupData && (
            <div className="mt-8 grid md:grid-cols-2 gap-8" data-testid="2fa-setup">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-3">Step 1 · Scan</div>
                <div className="bg-white border border-sand rounded-xl p-4 inline-block">
                  <QRCodeSVG value={setupData.otpauth_uri} size={180} bgColor="#FAF9F6" fgColor="#1A2520" />
                </div>
                <div className="text-xs text-sage mt-3 flex items-center gap-2">
                  <Smartphone className="w-3 h-3" strokeWidth={1.5} /> Or enter manually:
                </div>
                <button onClick={copyKey} className="font-mono text-xs text-ink mt-1.5 inline-flex items-center gap-2 hover:text-forest break-all" data-testid="copy-secret-btn">
                  {setupData.secret}
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              <form onSubmit={enable}>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mb-3">Step 2 · Verify</div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">6-digit code</label>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-mono text-2xl tracking-[0.5em] text-center focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                  placeholder="••••••" autoFocus data-testid="2fa-setup-code" />
                {error && <div className="mt-3 text-sm text-clay" data-testid="2fa-setup-error">{error}</div>}
                <div className="mt-4 flex gap-3">
                  <button type="button" onClick={() => { setStage("idle"); setError(""); setCode(""); }}
                    className="text-sm text-moss hover:text-ink" data-testid="2fa-cancel">Cancel</button>
                  <button type="submit" disabled={busy || code.length !== 6}
                    className="flex-1 bg-forest text-white rounded-lg px-5 py-3 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
                    data-testid="2fa-enable-submit">
                    {busy ? "Verifying…" : "Enable 2FA"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Disable */}
          {stage === "disable" && (
            <form onSubmit={disable} className="mt-6 max-w-sm" data-testid="2fa-disable-form">
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Enter current 6-digit code to confirm</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-mono text-2xl tracking-[0.5em] text-center focus:ring-2 focus:ring-clay focus:border-transparent outline-none"
                placeholder="••••••" autoFocus data-testid="2fa-disable-code" />
              {error && <div className="mt-3 text-sm text-clay" data-testid="2fa-disable-error">{error}</div>}
              <div className="mt-4 flex gap-3">
                <button type="button" onClick={() => { setStage("idle"); setError(""); setCode(""); }}
                  className="text-sm text-moss hover:text-ink" data-testid="2fa-disable-cancel">Cancel</button>
                <button type="submit" disabled={busy || code.length !== 6}
                  className="flex-1 bg-clay text-white rounded-lg px-5 py-3 font-medium hover:bg-[#9F4D3B] disabled:opacity-60 transition-colors"
                  data-testid="2fa-disable-submit">
                  {busy ? "Disabling…" : "Turn off 2FA"}
                </button>
              </div>
            </form>
          )}

          {/* Regenerate backup codes */}
          {stage === "regen" && (
            <form onSubmit={regenerate} className="mt-6 max-w-sm" data-testid="2fa-regen-form">
              <p className="text-sm text-moss leading-relaxed">
                New codes will be issued and all previous codes invalidated.
              </p>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-sage mt-4 block">Enter current 6-digit code</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="mt-2 w-full bg-cream border border-sand rounded-lg px-4 py-3 font-mono text-2xl tracking-[0.5em] text-center focus:ring-2 focus:ring-forest focus:border-transparent outline-none"
                placeholder="••••••" autoFocus data-testid="2fa-regen-code" />
              {error && <div className="mt-3 text-sm text-clay" data-testid="2fa-regen-error">{error}</div>}
              <div className="mt-4 flex gap-3">
                <button type="button" onClick={() => { setStage("idle"); setError(""); setCode(""); }}
                  className="text-sm text-moss hover:text-ink" data-testid="2fa-regen-cancel">Cancel</button>
                <button type="submit" disabled={busy || code.length !== 6}
                  className="flex-1 bg-forest text-white rounded-lg px-5 py-3 font-medium hover:bg-forest-deep disabled:opacity-60 transition-colors"
                  data-testid="2fa-regen-submit">
                  {busy ? "Issuing…" : "Generate new codes"}
                </button>
              </div>
            </form>
          )}

          {backupCodes && <BackupCodesPanel codes={backupCodes} onClose={() => setBackupCodes(null)} />}
        </div>
      </div>
    </div>
  );
}
